<template>
  <section
    ref="sectionRef"
    class="where-we-are"
    :class="{ 'where-we-are--visible': isVisible }"
    @mousemove="handleMouseMove"
    @mouseleave="resetImagePosition"
  >
    <div class="map-wrapper">
      <img
        :src="currentImage"
        alt="Где мы работаем"
        class="map-image"
        :style="imageStyle"
      />

      <div class="text-block">
        <span class="content-line"></span>

        <h2 class="title">
          Где мы работаем<span class="question">?</span>
        </h2>

        <p class="description">
          На данный момент проект доступен в этих регионах.
          Но основной фокус развития — Москва и Санкт-Петербург.
          Именно здесь мы активно расширяем базу пользователей
          и работодателей.
        </p>
      </div>
    </div>
  </section>

  <div class="separator"></div>
</template>

<script setup>

import {
  computed,
  onMounted,
  onUnmounted,
  ref,
} from "vue";

const lightImage = new URL(
  "../assets/where-we-are-light.webp",
  import.meta.url
).href;

const darkImage = new URL(
  "../assets/where-we-are-dark.webp",
  import.meta.url
).href;

const sectionRef = ref(null);
const isVisible = ref(false);

const theme = ref(
  document.documentElement.getAttribute("data-theme") ||
    "light"
);

const translateX = ref(0);
const translateY = ref(0);
const rotateX = ref(0);
const rotateY = ref(0);

const currentImage = computed(() => {
  return theme.value === "dark"
    ? darkImage
    : lightImage;
});

const imageStyle = computed(() => ({
  transform: `
    perspective(1400px)
    translate3d(
      ${translateX.value}px,
      ${translateY.value}px,
      0
    )
    rotateX(${rotateX.value}deg)
    rotateY(${rotateY.value}deg)
    scale(1.01)
  `,
}));

let themeObserver;
let visibilityObserver;

const handleMouseMove = (event) => {
  if (window.innerWidth <= 768) {
    return;
  }

  const section = sectionRef.value;

  if (!section) {
    return;
  }

  const rect = section.getBoundingClientRect();

  const relativeX =
    (event.clientX - rect.left) / rect.width;

  const relativeY =
    (event.clientY - rect.top) / rect.height;

  translateX.value =
    (relativeX - 0.5) * 6;

  translateY.value =
    (relativeY - 0.5) * 5;

  rotateY.value =
    (relativeX - 0.5) * 1.1;

  rotateX.value =
    (0.5 - relativeY) * 0.8;
};

const resetImagePosition = () => {
  translateX.value = 0;
  translateY.value = 0;
  rotateX.value = 0;
  rotateY.value = 0;
};

onMounted(() => {
  themeObserver = new MutationObserver(() => {
    theme.value =
      document.documentElement.getAttribute(
        "data-theme"
      ) || "light";
  });

  themeObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["data-theme"],
  });

  visibilityObserver = new IntersectionObserver(
    ([entry]) => {
      if (!entry.isIntersecting) {
        return;
      }

      isVisible.value = true;
      visibilityObserver?.disconnect();
    },
    {
      threshold: 0.18,
    }
  );

  if (sectionRef.value) {
    visibilityObserver.observe(sectionRef.value);
  }
});

onUnmounted(() => {
  themeObserver?.disconnect();
  visibilityObserver?.disconnect();
});
</script>

<style scoped>
.where-we-are {
  width: 1200px;
  margin: 0 auto;

  position: relative;

  overflow: hidden;
}

.map-wrapper {
  position: relative;

  width: 100%;

  opacity: 0;
  transform: translateY(24px);

  transition:
    opacity 0.9s ease,
    transform 0.9s cubic-bezier(0.22, 1, 0.36, 1);
}

.where-we-are--visible .map-wrapper {
  opacity: 1;
  transform: translateY(0);
}

.map-image {
  width: 100%;
  height: auto;

  display: block;

  object-fit: cover;

  border-radius: 24px;

  animation:
    map-breathing 7s ease-in-out infinite;

  transition:
    transform 0.35s cubic-bezier(0.22, 1, 0.36, 1),
    filter 0.3s ease,
    opacity 0.3s ease;

  will-change: transform;
}

.map-wrapper:hover .map-image {
  filter:
    saturate(1.03)
    contrast(1.015);
}

.text-block {
  position: absolute;

  top: 60px;
  left: 60px;

  max-width: 410px;

  padding-left: 22px;

  opacity: 0;
  transform: translateY(18px);

  transition:
    opacity 0.8s 0.16s ease,
    transform 0.8s 0.16s cubic-bezier(0.22, 1, 0.36, 1);
}

.where-we-are--visible .text-block {
  opacity: 1;
  transform: translateY(0);
}

.content-line {
  position: absolute;

  top: 5px;
  bottom: 2px;
  left: 0;

  width: 3px;

  border-radius: 999px;

  background:
    linear-gradient(
      180deg,
      #2277e6 0%,
      rgba(34, 119, 230, 0.08) 100%
    );

  transform: scaleY(0);
  transform-origin: top;

  transition:
    transform 0.8s 0.32s
      cubic-bezier(0.22, 1, 0.36, 1);
}

.where-we-are--visible .content-line {
  transform: scaleY(1);
}

.title {
  margin: 0 0 16px;

  display: inline-flex;
  align-items: baseline;
  gap: 4px;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 32px;
  line-height: 1.05;
  letter-spacing: -1.2px;

  color: var(--text);
}

.question {
  display: inline-block;

  color: #2277e6;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 1.18em;
  line-height: 1;

  animation:
    question-float 3.5s ease-in-out infinite;
}

.description {
  margin: 0;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 12px;
  line-height: 1.65;
  letter-spacing: -0.3px;

  color:
    color-mix(
      in srgb,
      var(--text) 58%,
      transparent
    );
}

.separator {
  width: 100%;
  height: 1px;

  margin-top: 10px;

  background-color: var(--separator_home);
}

@keyframes map-breathing {
  0%,
  100% {
    scale: 1;
  }

  50% {
    scale: 1.008;
  }
}

@keyframes question-float {
  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-2px);
  }
}

@media (max-width: 1240px) {
  .where-we-are {
    width: calc(100% - 48px);
  }
}

@media (max-width: 768px) {
  .where-we-are {
    width: 100%;

    padding: 0 16px;

    box-sizing: border-box;
  }

  .map-wrapper {
    display: flex;
    flex-direction: column;
    gap: 22px;

    transform: translateY(20px);
  }

  .where-we-are--visible .map-wrapper {
    transform: translateY(0);
  }

  .map-image {
    width: 100%;
    height: auto;

    border-radius: 16px;

    transform: none !important;

    animation-duration: 8s;
  }

  .text-block {
    position: relative;

    top: auto;
    left: auto;

    width: 100%;
    max-width: 100%;

    padding-left: 18px;

    box-sizing: border-box;
  }

  .title {
    margin-bottom: 14px;

    font-size: 24px;
    letter-spacing: -0.8px;
  }

  .description {
    font-size: 14px;
    line-height: 1.55;

    max-width: 100%;
  }

  .separator {
    margin-top: 36px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .map-wrapper,
  .map-image,
  .text-block,
  .content-line,
  .question {
    animation: none !important;
    transition-duration: 0.01ms !important;
  }

  .map-image {
    transform: none !important;
  }
}
</style>